import { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  IconButton, Badge, Menu, Box, Typography, Button, Divider, Snackbar, Alert
} from '@mui/material';
import { alpha } from '@mui/material/styles';
import NotificationsNoneOutlinedIcon from '@mui/icons-material/NotificationsNoneOutlined';
import VerifiedUserOutlinedIcon from '@mui/icons-material/VerifiedUserOutlined';
import VolumeOffOutlinedIcon from '@mui/icons-material/VolumeOffOutlined';
import FlagOutlinedIcon from '@mui/icons-material/FlagOutlined';
import CloudOutlinedIcon from '@mui/icons-material/CloudOutlined';
import PlaceOutlinedIcon from '@mui/icons-material/PlaceOutlined';
import { conectarSocketAdmin } from '../../utils/socket';
import adminAxios from '../../utils/adminAxios';

// "hace X" relativo simple (los avisos del panel son recientes)
const tiempoRel = (iso) => {
  if (!iso) return '';
  const s = Math.floor((Date.now() - new Date(iso).getTime()) / 1000);
  if (s < 60) return 'hace un momento';
  const m = Math.floor(s / 60); if (m < 60) return `hace ${m} min`;
  const h = Math.floor(m / 60); if (h < 24) return `hace ${h} h`;
  const d = Math.floor(h / 24); return `hace ${d} d`;
};

// Ícono de cada tipo de aviso (si llega uno que no se conoce se usa el de verificación)
const ICONO_AVISO = {
  CHAT_ABUSO: VolumeOffOutlinedIcon,
  REPORTE_CHAT: FlagOutlinedIcon,
  ARCHIVOS_PAUSA: CloudOutlinedIcon,
  CLOUDINARY_CREDITOS: CloudOutlinedIcon,
  ARCHIVOS_RETENCION: CloudOutlinedIcon,
  COORDENADAS: PlaceOutlinedIcon,
};

// Campana de notificaciones del superadmin. Trae los avisos por REST y escucha en vivo
// por Socket.IO (sala 'admins'). Llegan avisos de verificación de usuarios, de abuso del chat, reportes de conversaciones,
// del uso de archivos y de créditos de Cloudinary y de la renovación de las coordenadas de los mapas.
const AdminNotifications = () => {
  const navigate = useNavigate();
  const [lista, setLista] = useState([]);
  const [sinLeer, setSinLeer] = useState(0);
  const [anchor, setAnchor] = useState(null);
  const [toast, setToast] = useState(null);
  const socketRef = useRef(null);

  const cargar = useCallback(() => {
    adminAxios.get('/api/admin/notificaciones')
      .then(res => { setLista(res.data.notificaciones || []); setSinLeer(res.data.sinLeer || 0); })
      .catch(() => {});
  }, []);

  useEffect(() => {
    cargar();
    const socket = conectarSocketAdmin();
    socketRef.current = socket;
    socket.on('connect', () => socket.emit('join_admin'));
    socket.on('admin_notification', (notif) => {
      if (notif?.tipo && notif.tipo !== 'VERIFICACION') setToast(notif.titulo || 'Nuevo aviso');
      else setToast(notif?.usuarioNombre ? `${notif.usuarioNombre} ${notif.mensaje}` : 'Nueva verificación pendiente');
      cargar(); // re-sincroniza lista y conteo con el servidor (exacto)
    });
    return () => { socket.disconnect(); socketRef.current = null; };
  }, [cargar]);

  const abrir = (e) => { setAnchor(e.currentTarget); cargar(); };
  const cerrar = () => setAnchor(null);

  const abrirNotif = async (n) => {
    cerrar();
    if (!n.leida) {
      try { await adminAxios.patch(`/api/admin/notificaciones/${n._id}/leer`); } catch { /* ignore */ }
      setLista(prev => prev.map(x => x._id === n._id ? { ...x, leida: true } : x));
      setSinLeer(prev => Math.max(0, prev - 1));
    }
    navigate(n.enlace || '/admin/verificacion');
  };

  const leerTodas = async () => {
    try { await adminAxios.patch('/api/admin/notificaciones/leer-todas'); } catch { /* ignore */ }
    setLista(prev => prev.map(x => ({ ...x, leida: true })));
    setSinLeer(0);
  };

  return (
    <>
      <IconButton onClick={abrir} sx={{ bgcolor: 'white', border: 1, borderColor: 'sutil' }}>
        <Badge color="error" badgeContent={sinLeer} max={9}>
          <NotificationsNoneOutlinedIcon />
        </Badge>
      </IconButton>

      <Menu
        anchorEl={anchor} open={!!anchor} onClose={cerrar}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
        PaperProps={{ sx: { width: 370, maxHeight: 460, borderRadius: 3, mt: 1, overflow: 'hidden' } }}
        MenuListProps={{ sx: { p: 0 } }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', px: 2, py: 1.5 }}>
          <Typography sx={{ fontWeight: 800, color: 'primary.main' }}>Notificaciones</Typography>
          {sinLeer > 0 && (
            <Button size="small" onClick={leerTodas} sx={{ textTransform: 'none', fontSize: 12, color: 'primary.main' }}>
              Marcar todas como leídas
            </Button>
          )}
        </Box>
        <Divider />

        {lista.length === 0 ? (
          <Box sx={{ p: 4, textAlign: 'center', color: 'text.secondary' }}>
            <NotificationsNoneOutlinedIcon sx={{ fontSize: 36, mb: 1 }} />
            <Typography sx={{ fontSize: 13 }}>Sin notificaciones</Typography>
          </Box>
        ) : (
          <Box sx={{ maxHeight: 380, overflowY: 'auto' }}>
            {lista.map(n => {
              const Icono = ICONO_AVISO[n.tipo] || VerifiedUserOutlinedIcon;
              return (
              <Box
                key={n._id} onClick={() => abrirNotif(n)}
                sx={{
                  display: 'flex', gap: 1.5, px: 2, py: 1.5, cursor: 'pointer',
                  bgcolor: n.leida ? 'transparent' : ((t) => alpha(t.palette.secondary.main, 0.05)),
                  '&:hover': { bgcolor: (t) => alpha(t.palette.secondary.main, 0.12) }, borderBottom: 1, borderColor: 'sutil',
                }}
              >
                <Box sx={{ width: 34, height: 34, borderRadius: 2, bgcolor: (t) => alpha(t.palette.secondary.main, 0.12), color: 'secondary.main', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                  <Icono sx={{ fontSize: 18 }} />
                </Box>
                <Box sx={{ minWidth: 0, flexGrow: 1 }}>
                  <Typography sx={{ fontSize: 13, fontWeight: 700, color: 'primary.main' }}>{n.titulo}</Typography>
                  <Typography sx={{ fontSize: 12.5, color: 'text.secondary' }}>
                    {n.usuarioNombre && <strong>{n.usuarioNombre} </strong>}{n.mensaje}
                  </Typography>
                  <Typography sx={{ fontSize: 11, color: 'text.secondary', mt: 0.25 }}>{tiempoRel(n.updatedAt || n.createdAt)}</Typography>
                </Box>
                {!n.leida && <Box sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: 'secondary.main', mt: 0.5, flexShrink: 0 }} />}
              </Box>
              );
            })}
          </Box>
        )}
      </Menu>

      <Snackbar open={!!toast} autoHideDuration={5000} onClose={() => setToast(null)} anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}>
        {toast ? <Alert severity="info" icon={<VerifiedUserOutlinedIcon />} onClose={() => setToast(null)} sx={{ borderRadius: 2 }}>{toast}</Alert> : undefined}
      </Snackbar>
    </>
  );
};

export default AdminNotifications;
